import { useState, useMemo } from "react";
import { Gamepad2, Loader2 } from "lucide-react";
import BoardGameList from "@/components/app/BoardGameList";
import SyncBggButton from "@/components/app/SyncBggButton";

const LIBRARY_TABS = [
  { key: "owned",        label: "Owned",       flag: "own" },
  { key: "want_to_play", label: "Want to Play", flag: "want_to_play" },
  { key: "wishlist",     label: "Wishlist",     flag: "wishlist" },
];

/**
 * Shared "My Collection" section used on the dashboard, profile, and
 * the event page's "My Collection" tab.
 *
 * Props:
 *   games        array   — library entries for the current user
 *   heading      string  — section heading (pass null to suppress)
 *   showTabs     bool    — show Owned/Want to Play/Wishlist tabs (dashboard & profile only)
 *   showSyncButton bool  — show sync button
 *   ...rest              — forwarded to BoardGameList (mode, onBring, onWantToPlay, etc.)
 */
export default function MyLibrarySection({
  games = [],
  heading = "Game Library",
  showSyncButton = true,
  showTabs = false,
  ...rest
}) {
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState("owned");

  const counts = useMemo(() => ({
    owned:        games.filter((g) => g.own).length,
    want_to_play: games.filter((g) => g.want_to_play).length,
    wishlist:     games.filter((g) => g.wishlist).length,
  }), [games]);

  const displayedGames = useMemo(() => {
    if (!showTabs) return games;
    const tab = LIBRARY_TABS.find((t) => t.key === activeTab);
    return tab ? games.filter((g) => g[tab.flag]) : games;
  }, [games, activeTab, showTabs]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        {heading ? (
          <div className="flex items-center gap-2">
            <Gamepad2 className="h-5 w-5 text-muted-foreground" />
            <h2 className="font-semibold">{heading}</h2>
          </div>
        ) : (
          <span />
        )}
        {showSyncButton && <SyncBggButton onSyncingChange={setSyncing} />}
      </div>

      {syncing && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-1">
          <Loader2 className="h-4 w-4 animate-spin" />
          Syncing your BGG library…
        </div>
      )}

      {showTabs && (
        <div className="flex items-center gap-1 border-b border-border">
          {LIBRARY_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === tab.key
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab.label}
              <span className={`ml-1 text-xs rounded-full px-1.5 py-0.5 ${
                activeTab === tab.key
                  ? "bg-primary/15 text-primary"
                  : "bg-muted text-muted-foreground"
              }`}>
                {counts[tab.key]}
              </span>
            </button>
          ))}
        </div>
      )}

      <BoardGameList games={displayedGames} mode="library" {...rest} />
    </div>
  );
}
